import { useMemo, useCallback } from "react";

import { MOSAIC_VIEW_DEFAULT_LIMIT } from "enums/categories";
import { PAGE_MODULES } from "enums/pageModules";
import AlgoliaCardsPresenter from "presenters/AlgoliaCardsPresenter";
import CategoryRowPresenter from "presenters/CategoryRowPresenter";
import FeaturePanelPresenter from "presenters/FeaturePanelPresenter";

import useRouter from "./useRouter";
import useAlgolia from "./useAlgolia";

const usePageModules = () => {
  const { location } = useRouter();
  const { filterValues, hasFacetFilterValues } = useAlgolia();
  const SUPPORTED_MODULES_WITH_CONFIGURE = [
    PAGE_MODULES.ALGOLIA_CARDS,
    PAGE_MODULES.CATEGORY_ROW,
    PAGE_MODULES.MODULE_SLIDER,
  ];

  const processAlgoliaCardsModule = useCallback(
    (module) => {
      const { query } = module;
      const { facetFilters } = query;
      const isMosaic = AlgoliaCardsPresenter.isMosaic(module);
      const hitsPerPage = isMosaic ? MOSAIC_VIEW_DEFAULT_LIMIT : AlgoliaCardsPresenter.limit(module);
      const { contentTypeFiltersForPath, numberOfPeopleFiltersForPath, sortFilterSelectedForPath } = filterValues(
        location.pathname,
      );
      const hasFacetFilters = hasFacetFilterValues(location.pathname);
      const collectionsFilter = facetFilters?.find((facetFilter) =>
        facetFilter?.some((f) => f?.includes("_collections:")),
      );

      const filterValuesForMosaic = hasFacetFilters
        ? [...contentTypeFiltersForPath, ...numberOfPeopleFiltersForPath, collectionsFilter]
        : facetFilters;

      const filters = isMosaic ? filterValuesForMosaic : facetFilters;
      const sortBy = isMosaic ? sortFilterSelectedForPath : undefined;

      const state = {
        ...module,
        configure: {
          sendTokenOnlyForPaidUser: true,
          facetFilters: filters,
          hitsPerPage,
        },
        sortBy,
      };
      return state;
    },
    [filterValues, location.pathname],
  );

  const processCategoryRowModule = useCallback((module) => {
    const algoliaCard = CategoryRowPresenter.algoliaCardFromPageModule(module);
    const algoliaLimit = AlgoliaCardsPresenter.limit(algoliaCard);
    const hitsPerPage = CategoryRowPresenter.hitsPerPage(module, algoliaLimit);
    return {
      id: module.id,
      ...algoliaCard,
      configure: {
        hitsPerPage,
      },
    };
  }, []);

  const processModuleSliderModule = useCallback((module) => {
    const algoliaCard = FeaturePanelPresenter.algoliaCardFromCards(module);
    const algoliaLimit = AlgoliaCardsPresenter.limit(algoliaCard);
    const hitsPerPage = FeaturePanelPresenter.hitsPerPage(module, algoliaLimit);
    return {
      id: module.id,
      ...algoliaCard,
      configure: {
        hitsPerPage,
      },
    };
  }, []);

  const moduleProcessors = useMemo(
    () => ({
      [PAGE_MODULES.ALGOLIA_CARDS]: processAlgoliaCardsModule,
      [PAGE_MODULES.CATEGORY_ROW]: processCategoryRowModule,
      [PAGE_MODULES.MODULE_SLIDER]: processModuleSliderModule,
    }),
    [processAlgoliaCardsModule, processCategoryRowModule, processModuleSliderModule],
  );

  const getPageModulesConfiguration = useCallback((pageModules) => {
    if (!pageModules?.length) return undefined;

    return pageModules
      .filter((module) => SUPPORTED_MODULES_WITH_CONFIGURE.includes(module.type))
      .map((module) => moduleProcessors[module.type](module));
  }, []);

  return {
    getPageModulesConfiguration,
    moduleProcessors,
    processAlgoliaCardsModule,
    processCategoryRowModule,
    processModuleSliderModule,
  };
};

export default usePageModules;
